/**
 * Chiave principale — tappa 2 (2026-09-10)
 *
 * Vedi ENCRYPTION-PLAN.md, sezioni 2, 3, 3-ter e 7-bis.
 *
 * Oggi la chiave che cifra i file di un utente E' PBKDF2(password, salt).
 * Cambiare la password cambia la chiave, e i file diventano illeggibili: per
 * questo il cambio password e il ripristino da chiave di recupero sono spenti
 * dal 24 agosto. Questo modulo separa le due cose:
 *
 *   master key   — la chiave che apre i file. Non cambia mai.
 *   wrapping key — PBKDF2(password, wrap_salt). Protegge la master key.
 *                  Puo' essere sostituita quante volte si vuole.
 *
 * DOVE SIAMO (2026-09-16). La busta si scrive a CHIUNQUE non ce l'abbia, al
 * primo accesso. Il CAMBIO PASSWORD resta chiuso a tutti tranne chi e' in
 * CHIAVE_PRINCIPALE_UTENTI del backend: e' il server a decidere e a dirlo al
 * client (`user.puoCambiarePassword`), con 403 a chi non c'e'. Avere la
 * busta e' la condizione per poter cambiare password un giorno, non il
 * permesso di farlo oggi.
 */

import { generateSalt, deriveKey, wrapKey, unwrapKey, decryptBuffer, base64ToBuffer } from './crypto';
import { apiMigraChiavePrincipale, apiCampioni, apiMigrazioneRifiutata } from './api';

// ── L'interruttore ────────────────────────────────────────────────────────
// Elenco di username separati da virgola. Vuoto (il caso normale) = nessuno.
// Non e' una percentuale e non e' un flag booleano di proposito: durante le
// prove si deve poter dire ESATTAMENTE su quale account sta girando, e la
// risposta deve essere leggibile senza aprire il codice.
// LE DUE VARIABILI DI BUILD NON ESISTONO PIU' (2026-09-16). REACT_APP_CAMBIO_
// PASSWORD_ATTIVO e REACT_APP_CHIAVE_PRINCIPALE_UTENTI non stavano in nessun
// file: ogni build dal 12/09 le perdeva e spegneva profilo e recupero senza
// dirlo. Oggi decide il server: `user.puoCambiarePassword` (safeUser) per il
// profilo, GET /auth/interruttori per la pagina di accesso.

// ── Aprire la chiave dei file ─────────────────────────────────────────────
/**
 * Restituisce la chiave con cui si cifrano e decifrano i file di questo
 * utente. E' l'unica funzione che i sei punti di derivazione devono chiamare
 * al posto di `deriveKey(password, salt)`.
 *
 * Due strade, e la scelta non e' una preferenza: dipende da cosa c'e' sulla
 * riga dell'utente.
 *
 *   busta presente  -> deriva la wrapping key da (password, wrap_salt) e
 *                      scarta la master key. E' lo schema nuovo.
 *   busta assente   -> PBKDF2(password, salt), IDENTICO a oggi. E' anche il
 *                      comportamento di ripiego se qualcosa va storto: un
 *                      utente senza busta non e' un utente rotto, e' un
 *                      utente non ancora migrato.
 */
export class BustaNonApribile extends Error {
  constructor() {
    super('busta non apribile');
    this.name = 'BustaNonApribile';
  }
}

export async function apriChiaveUtente(password, saltLegacy, utente) {
  // LA SCELTA DELLA CHIAVE NON DIPENDE DALL'ELENCO (2026-09-12, fase 1).
  // Prima la condizione era `utenteAbilitato(utente) && busta && wrapSalt`:
  // chi aveva la busta ma usciva dall'elenco sarebbe caduto nel ripiego
  // PBKDF2(password, salt) — cioe' avrebbe derivato la chiave SBAGLIATA, in
  // silenzio, e non avrebbe aperto piu' un file. Bastava una modifica
  // all'elenco per rompere chi era gia' migrato.
  // Adesso comanda il dato: se la busta c'e', si apre la busta. L'elenco
  // decide chi MIGRA e chi vede il modulo, mai con quale chiave si apre un
  // file. Oggi non cambia niente per nessuno — i due account con la busta
  // sono entrambi in elenco — ed e' la condizione per la fase 2.
  if (utente?.wrappedMasterKey && utente?.wrapSalt) {
    const wrappingKey = await deriveKey(password, utente.wrapSalt);
    try {
      return await unwrapKey(utente.wrappedMasterKey, wrappingKey);
    } catch {
      // NESSUN RIPIEGO sulla derivazione vecchia, ed e' una decisione presa
      // apposta (2026-09-10). Il ripiego funzionerebbe oggi — la chiave
      // derivata e' ancora quella giusta — e smetterebbe di funzionare il
      // giorno in cui si accende il cambio password, senza che nessuno se ne
      // accorga: l'utente vedrebbe la cassaforte aprirsi e i file non
      // decifrarsi. Un errore visibile oggi vale piu' di un danno invisibile
      // domani.
      throw new BustaNonApribile();
    }
  }
  // IL RIPIEGO NON E' PIU' INCONDIZIONATO (2026-09-10).
  //
  // `masterKeyDefinitiva` dice che questo utente una busta ce l'ha gia' avuta.
  // Se ce l'ha avuta e adesso non c'e', la riga e' incoerente: succede solo
  // per una modifica manuale al database o un ripristino parziale. In quello
  // stato PBKDF2(password, salt) NON e' piu' la master key — il salt e' stato
  // ruotato da un cambio password e la chiave no — e derivarla darebbe una
  // chiave sbagliata senza un errore.
  //
  // E' esattamente quello che e' successo il 2026-09-10 su un account di
  // prova: busta tolta a mano, e il primo accesso l'ha ricreata con la chiave
  // sbagliata, marcandola verificata.
  // Anche questa guardia non dipende piu' dall'elenco (2026-09-12): una riga
  // incoerente e' pericolosa per chiunque, non solo per chi e' in elenco.
  if (utente?.masterKeyDefinitiva) throw new BustaNonApribile();

  return deriveKey(password, saltLegacy);
}

// ── Provare la chiave su file VERI ────────────────────────────────────────
/**
 * Tre esiti, e sono tre cose diverse che vanno dette in modo diverso.
 *
 *   'apre'      tutti i campioni si aprono. La chiave e' quella giusta:
 *               AES-GCM autentica, quindi non e' una probabilita'.
 *   'non-apre'  NESSUN campione si apre. La chiave e' sbagliata.
 *   'discorde'  uno si' e uno no. NON e' un problema di chiave — con la
 *               chiave sbagliata non se ne aprirebbe nessuno. E' un file
 *               corrotto sul disco, OPPURE un account con i file spezzati in
 *               due (parte cifrata con una chiave, parte con un'altra: capita
 *               a chi passo' dal ripristino rotto di prima del 24 agosto).
 *               In entrambi i casi NON si migra, ma il motivo va detto per
 *               quello che e': il secondo caso e' molto piu' grave del primo,
 *               e confonderli fa perdere l'unica occasione di accorgersene.
 *   'nessuno'   non c'era niente da provare.
 */
export async function provaChiaveSuCampioni(chiave, campioni) {
  if (!campioni || !campioni.length) return { esito: 'nessuno', aperti: 0, totali: 0 };
  let aperti = 0;
  const dettaglio = [];
  for (const c of campioni) {
    let ok = false;
    try {
      await decryptBuffer(chiave, base64ToBuffer(c.cifratoBase64), c.iv);
      ok = true; aperti++;
    } catch { ok = false; }
    dettaglio.push({ id: c.id, caricatoIl: c.caricatoIl, apre: ok });
  }
  const esito = aperti === campioni.length ? 'apre' : (aperti === 0 ? 'non-apre' : 'discorde');
  return { esito, aperti, totali: campioni.length, dettaglio };
}

// ── Dire che e' andata male ───────────────────────────────────────────────
//
// Quando la chiave non apre i file dell'utente, questa e' l'unica occasione
// in cui il sistema puo' SAPERE che quell'account ha un problema: la prova
// si fa qui, nel browser, con la password in mano, e il server non puo'
// rifarla da solo. Senza questa riga il momento passa e non resta niente.
//
// A vuoto e senza await di proposito: non deve rallentare l'accesso, e se il
// registro non riceve la riga l'utente non se ne accorge. Nel messaggio non
// va NIENTE di decifrato — solo quanti campioni si sono provati, quanti si
// sono aperti, e le date di caricamento, che il server ha gia'.
function segnalaRifiuto(esito, prova, via, errore) {
  try {
    apiMigrazioneRifiutata({
      esito, via,
      campioni: prova ? prova.totali : null,
      aperti: prova ? prova.aperti : null,
      dettaglio: errore
        || (prova && prova.dettaglio
            ? prova.dettaglio.map((d) => `${d.id.slice(0, 8)}:${d.apre ? 'apre' : 'no'}@${d.caricatoIl || '?'}`).join(' ')
            : null),
    });
  } catch { /* vedi proprieta' 1: mai bloccante */ }
}

// ── Migrare: incartare la chiave che l'utente gia' usa ────────────────────
/**
 * Da chiamare DOPO aver ottenuto la chiave, con la password ancora in mano.
 *
 * Tre proprieta' che non sono negoziabili, e il motivo di ciascuna:
 *
 * 1. NON BLOCCANTE. Qualunque cosa vada storta qui, l'accesso dell'utente e'
 *    gia' avvenuto e la sua chiave e' gia' in memoria. Un errore di rete non
 *    deve trasformarsi in un login fallito: si riprova al prossimo accesso.
 *    Stesso principio gia' adottato per db.addFileLimitHit in routes/files.js.
 *
 * 2. VERIFICA PRIMA DI SALVARE (sezione 7-bis). La busta viene RIAPERTA qui,
 *    e i byte che ne escono vengono confrontati con quelli della chiave vera.
 *    Se non coincidono la busta non parte affatto. Senza questo controllo
 *    "la busta esiste" e "la busta si apre" sarebbero indistinguibili, e la
 *    differenza si scoprirebbe il giorno del cambio password, cioe' troppo
 *    tardi per rimediare.
 *
 * 3. IDEMPOTENTE. Non e' mai una lettura-modifica-scrittura: e' sempre
 *    "genera una busta nuova e sovrascrivi". Due accessi in parallelo
 *    producono due buste ugualmente valide della stessa chiave.
 */
export async function migraSeServe({ password, chiave, utente, via }) {
  // FASE 2 (2026-09-12): IL CANCELLO E' APERTO PER TUTTI.
  //
  // Qui stava `if (!utenteAbilitato(utente)) return { esito: 'spento' }`.
  // Non c'e' piu': la busta si scrive a CHIUNQUE non ce l'abbia, al primo
  // accesso utile. E' l'operazione piu' innocua di tutta la cifratura, e
  // vale la pena dire perche':
  //
  //   la busta INCARTA la chiave che questo utente ha gia', senza cambiarne
  //   il valore di un bit. Non tocca `salt`, non tocca la password, non
  //   ricifra un solo file. Verificato estraendo la chiave dalla busta e
  //   confrontandola byte per byte con PBKDF2(password, salt): identica.
  //
  // Per questo anche chi usa la VECCHIA app Android — che della busta non sa
  // niente, e continua a derivare PBKDF2(password, salt) — apre i suoi file
  // esattamente come prima: provato end-to-end, file cifrato dalla vecchia
  // app e riletto dopo la migrazione, contenuto identico.
  //
  // COSA NON SI APRE INSIEME A QUESTO: il permesso di cambiare password.
  // Sono due cose diverse e restano separate — CHIAVE_PRINCIPALE_UTENTI resta
  // ai quattro account di prova, e il server risponde 403 a tutti gli altri.
  // Avere la busta e' la CONDIZIONE per poter cambiare password un giorno,
  // non il permesso di farlo oggi.
  //
  // Le difese che contano non erano l'elenco e sono ancora tutte qui sotto:
  // la busta deve riaprirsi, e la chiave che ne esce deve aprire davvero i
  // file di prova di questo utente. Se qualcosa non torna non si scrive
  // niente e si riprova al prossimo accesso.
  if (utente?.wrappedMasterKey) return { esito: 'gia-migrato' };
  if (!password || !chiave) return { esito: 'dati-mancanti' };

  try {
    // Salt NUOVO e indipendente: non si puo' incartare una chiave con se
    // stessa. Se la wrapping key nascesse da (password, salt) — lo stesso
    // input della master key — sarebbe lo stesso identico valore, e
    // l'incarto non separerebbe niente. Stessa password, salt diverso.
    const wrapSalt = generateSalt();
    const wrappingKey = await deriveKey(password, wrapSalt);
    const busta = await wrapKey(chiave, wrappingKey);

    const verificata = await bustaSiApre(busta, wrappingKey, chiave);
    if (!verificata) {
      // Non si salva una busta che non si e' riusciti a riaprire. L'utente
      // resta non migrato e riprova al prossimo accesso: e' lo stato sicuro.
      console.error('chiave principale: la busta non si riapre, migrazione annullata');
      segnalaRifiuto('verifica-fallita', null, via);
      return { esito: 'verifica-fallita' };
    }

    // LA PROVA CHE MANCAVA (2026-09-10). Sopra si e' verificato che la busta
    // si RIAPRA. Qui si verifica che la chiave dentro sia quella GIUSTA, ed e'
    // un'altra cosa: il 10 settembre una busta perfetta e' stata scritta con
    // dentro una chiave che non apriva un solo file, e nessun controllo se ne
    // era accorto.
    const risposta = await apiCampioni();
    const prova = await provaChiaveSuCampioni(chiave, risposta.campioni);
    if (prova.esito === 'non-apre') {
      console.error('chiave principale: la chiave non apre nessuno dei file di prova — migrazione annullata');
      segnalaRifiuto('chiave-sbagliata', prova, via);
      return { esito: 'chiave-sbagliata', prova };
    }
    if (prova.esito === 'discorde') {
      // Non si migra, ma questo NON e' "chiave sbagliata": con la chiave
      // sbagliata non si aprirebbe niente. O un file e' corrotto sul disco, o
      // i file di questo account sono cifrati con due chiavi diverse. La
      // seconda e' molto piu' grave e va guardata da una persona.
      console.error('chiave principale: campioni DISCORDI, migrazione annullata — file corrotto o account con due chiavi', prova.dettaglio);
      segnalaRifiuto('campioni-discordi', prova, via);
      return { esito: 'campioni-discordi', prova };
    }
    // 'nessuno' passa: un account senza file non ha niente da perdere, e la
    // busta gli serve comunque per i caricamenti futuri.

    await apiMigraChiavePrincipale({ wrapSalt, wrappedMasterKey: busta, verificata: true, via });
    return { esito: 'migrato', via };
  } catch (err) {
    // Volutamente inghiottito: vedi proprieta' 1.
    console.error('chiave principale: migrazione non riuscita (si riprova al prossimo accesso)', err);
    segnalaRifiuto('errore', null, via, String(err && err.message));
    return { esito: 'errore', errore: String(err && err.message) };
  }
}

/**
 * Riapre la busta e confronta i byte con quelli della chiave vera.
 *
 * Il confronto e' sui byte grezzi, non sull'oggetto CryptoKey: due CryptoKey
 * distinte con lo stesso materiale sono oggetti diversi e `===` sarebbe
 * sempre falso, cioe' un controllo che fallisce sempre — che e' peggio di
 * nessun controllo, perche' sembra rigoroso.
 */
async function bustaSiApre(busta, wrappingKey, chiaveVera) {
  try {
    const riaperta = await unwrapKey(busta, wrappingKey);
    const a = new Uint8Array(await crypto.subtle.exportKey('raw', riaperta));
    const b = new Uint8Array(await crypto.subtle.exportKey('raw', chiaveVera));
    if (a.length !== b.length || a.length === 0) return false;
    // Confronto a tempo costante: qui non protegge da un attacco (siamo nel
    // browser dell'utente, con entrambe le chiavi in chiaro in memoria), ma
    // costa niente e non lascia un'abitudine sbagliata in giro per il codice.
    let diff = 0;
    for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i];
    return diff === 0;
  } catch {
    return false;
  }
}

// ── TAPPA 3 — cambiare la password senza perdere i file ───────────────────
/**
 * Incarta la STESSA master key con una password nuova.
 *
 * E' il cuore della tappa 3, ed e' tre righe: salt nuovo, wrapping key nuova,
 * stessa chiave dentro. La master key non viene rigenerata e nessun file
 * viene letto, riscritto o ricifrato — e' la proprieta' su cui poggia tutto
 * il piano (ENCRYPTION-PLAN.md 2.5 e 6.2).
 *
 * Lancia BustaNonApribile se la busta nuova non si riapre: chi chiama NON
 * deve mandare al server niente che non sia stato verificato qui.
 */
export async function reincartaConNuovaPassword(masterKey, nuovaPassword) {
  const wrapSalt = generateSalt();
  const wrappingKey = await deriveKey(nuovaPassword, wrapSalt);
  const busta = await wrapKey(masterKey, wrappingKey);
  if (!(await bustaSiApre(busta, wrappingKey, masterKey))) throw new BustaNonApribile();
  return { wrapSalt, wrappedMasterKey: busta, verificata: true };
}

/**
 * Recupera la master key dalla copia protetta con la chiave di recupero.
 *
 * Restituisce la chiave SOLO se la busta si apre davvero. AES-GCM autentica:
 * se la decifratura riesce, i byte che escono sono esattamente quelli che
 * erano stati incartati — non "probabilmente".
 *
 * ATTENZIONE, ed e' il limite da conoscere: questo dimostra che la copia e'
 * integra, NON che la chiave dentro sia quella giusta. Se la copia fu creata
 * a suo tempo da una chiave sbagliata (i 43 account di 5-bis del piano),
 * GCM non se ne accorge. Per questo chi chiama deve provarla su un file
 * VERO prima di sovrascrivere la password: vedi apreUnFileVero().
 *
 * NOTA su quale primitiva si usa, e non e' un dettaglio di stile: qui si
 * chiama unwrapKey() e NON decryptKeyFromRecovery(), che pure fa la stessa
 * identica decifratura sullo stesso identico formato. La differenza e' una
 * parola sola: decryptKeyFromRecovery importa la chiave come NON estraibile,
 * e questa chiave dev'essere ri-esportata subito dopo per essere incartata
 * con la password nuova. Con quella non si potrebbe, e il ripristino
 * fallirebbe alla riga successiva — dentro un flusso che ha appena promesso
 * all'utente di riportarlo dai suoi file.
 */
export async function recuperaChiaveDaBackup(encryptedKeyBackup, recoveryKey) {
  try {
    return await unwrapKey(encryptedKeyBackup, recoveryKey);
  } catch {
    throw new BustaNonApribile();
  }
}

// apreUnFileVero() e' stata tolta il 2026-09-10: faceva la stessa cosa su UN
// campione solo, e un campione solo non sa distinguere "chiave sbagliata" da
// "file corrotto". La sostituisce provaChiaveSuCampioni(), qui sopra.

/**
 * ACCOUNT NUOVI — perche' in questa tappa NON generano una master key casuale.
 *
 * Lo schema finale (sezione 2, punto 1) vuole che un account nuovo nasca con
 * una chiave casuale, non derivata dalla password. E' giusto, ma non e'
 * ancora sicuro farlo, e il motivo e' l'ordine delle scritture:
 *
 *   oggi  register crea l'utente, POI il client chiama /migrate-master-key.
 *         Sono due scritture separate, e la seconda puo' fallire.
 *
 * Con una master key CASUALE, se la seconda scrittura fallisce l'account
 * resta senza busta e la chiave casuale muore con la scheda del browser:
 * i file appena caricati diventano illeggibili per sempre. Con la master key
 * = PBKDF2(password, salt), lo stesso fallimento non fa alcun danno —
 * l'utente e' semplicemente un utente non migrato, e la strada di ripiego di
 * `apriChiaveUtente` lo apre lo stesso.
 *
 * Quindi in tappa 2 anche register e redeemStackSocialCode migrano come tutti
 * gli altri. La chiave casuale diventa sicura quando la busta viene scritta
 * DENTRO la stessa transazione che crea l'utente (POST /auth/register che
 * accetta wrapSalt e wrappedMasterKey), e non un istante prima.
 */
